import {
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';

import { Checkmark, Close } from '@mint-lib/icons';
import { Button, InputWithIcon } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

type Props = {
  initialValue?: string;
  onCommit: (name: string) => boolean;
  onCancel: () => void;
  validateName: (name: string) => boolean;
  cancelOnBlur?: boolean;
  className?: string;
};

const FolderNameInput = ({
  initialValue = '',
  onCommit,
  onCancel,
  validateName,
  cancelOnBlur = true,
  className,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [name, setName] = useState(initialValue);

  const isValid = validateName(name);

  const handleCommit = () => {
    if (!isValid) return;

    onCommit(name);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleCommit();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (!cancelOnBlur) return;
    if (containerRef.current?.contains(event.relatedTarget)) return;

    onCancel();
  };

  const keepFocus = (event: MouseEvent) => event.preventDefault();

  return (
    <div ref={containerRef} className="w-full">
      <InputWithIcon
        className={cn('h-8', className)}
        autoFocus
        size="small"
        value={name}
        aria-label="folder-name"
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
        icon={
          <div className="flex flex-row gap-2">
            <Button
              icon
              variant="plain"
              size="small"
              disabled={!isValid}
              aria-label="confirm-folder-name"
              data-dd-action-name="Knowledge base: confirm folder name"
              onMouseDown={keepFocus}
              onClick={handleCommit}
            >
              <Checkmark />
            </Button>
            <Button
              icon
              variant="plain"
              size="small"
              aria-label="cancel-folder-name"
              data-dd-action-name="Knowledge base: cancel folder name"
              onMouseDown={keepFocus}
              onClick={onCancel}
            >
              <Close className="fill-error-dark" />
            </Button>
          </div>
        }
      />
    </div>
  );
};

export { FolderNameInput };
